/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Button,
    useMultiStyleConfig,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {pxToRem} from '../../../utills/pxToRem'
import {fonts} from '../../../theme/foundations/fonts'
import {accordionListBtnStyles} from './refinements.style'
import useScrollToMoreButton from '../../../hooks/useScrollToMoreButton'

const SizeRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const {formatMessage} = useIntl()
    // const styles = useMultiStyleConfig('SwatchGroup', {
    //     variant: 'square',
    //     disabled: false
    // })

    const [showAll, setShowAll] = useState(false)
    const {setScrollToMore, moreButtonRef} = useScrollToMoreButton()

    const getInitialSliceCount = (attribute) => {
        switch (attribute) {
            case 'c_size':
                return 15
            case 'c_width':
                return 15
            default:
                return filter.values.length
        }
    }

    const newValues = filter?.values?.filter(
        (item) => !(item.hitCount === 0 && !selectedFilters.includes(item.value))
    )
    filter.values = newValues

    const initialSliceCount = getInitialSliceCount(filter.attributeId)
    const displayedValues = showAll ? filter.values : filter.values.slice(0, initialSliceCount)

    const getToggleMessage = () => {
        return showAll ? `Less ${filter.label}s` : `More ${filter.label}s`
    }

    const toggleShowAllFilters = () => {
        setScrollToMore(!!showAll)
        setShowAll(!showAll)
    }

    return (
        <Box pl={pxToRem(10)}>
            {displayedValues?.map((value, idx) => {
                // Note the loose comparison, for "string == number" checks.
                const isSelected = selectedFilters.some((filterValue) => filterValue == value.value)

                return (
                    <>
                        <Button
                            key={idx}
                            borderColor={isSelected ? 'black' : 'gray.200'}
                            backgroundColor={isSelected ? 'black' : 'white'}
                            color={isSelected ? 'white' : 'gray.900'}
                            onClick={() => toggleFilter(value, filter.attributeId, isSelected)}
                            aria-checked={isSelected}
                            variant="outline"
                            textAlign={'center'}
                            minWidth={pxToRem(35)}
                            height={pxToRem(25)}
                            marginRight={[pxToRem(10), pxToRem(10), pxToRem(10), pxToRem(10)]}
                            marginBottom={[pxToRem(10), pxToRem(10), pxToRem(10), pxToRem(10)]}
                            paddingLeft={pxToRem(4)}
                            paddingRight={pxToRem(4)}
                            border={`${pxToRem(1)} solid black`}
                            borderRadius={0}
                            fontFamily={fonts.primary}
                            fontSize={[
                                `${pxToRem(10)}`,
                                `${pxToRem(10)}`,
                                `${pxToRem(12)}`,
                                `${pxToRem(12)}`
                            ]}
                            fontStyle={'normal'}
                            fontWeight={400}
                            lineHeight={'normal'}
                            letterSpacing={`${pxToRem(1)}`}
                            textTransform={'uppercase'}
                            aria-label={formatMessage(
                                isSelected ? REMOVE_FILTER : ADD_FILTER,
                                value
                            )}
                            sx={{
                                '@media (hover: hover) and (pointer: fine)': {
                                    '&:hover': {
                                        backgroundColor: 'black',
                                        color: 'white'
                                    }
                                },
                                '@media (hover: none)': {
                                    '&:hover': {
                                        backgroundColor: isSelected ? 'black' : 'white',
                                        color: isSelected ? 'white' : 'gray.900'
                                    }
                                }
                            }}
                        >
                            {value.label}
                        </Button>
                    </>
                )
            })}
            {filter.values.length > initialSliceCount && (
                <Box ref={moreButtonRef} onClick={toggleShowAllFilters} sx={accordionListBtnStyles}>
                    {getToggleMessage()}
                </Box>
            )}
        </Box>
    )
}

SizeRefinements.propTypes = {
    filter: PropTypes.object,
    selectedFilters: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    toggleFilter: PropTypes.func
}

export default SizeRefinements
